import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate, useForm } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  NumberInput,
  RegularModal,
  Select,
  TimePicker,
  UserSelect,
} from '@/components_deprecated'
import { FAIcon, NumberInput as NullableNumberInput } from '@/components'
import VehicleSelect from '@/components/vehicle-select'
import {
  set,
  differenceInCalendarDays,
  differenceInWeeks,
  startOfDay,
} from 'date-fns'
import type {
  VehicleExpirationNode,
  VehicleExpirationTypeShallow,
} from '@/modules/vehicles'
import { Input, Field, Form } from '@/modules/users/user-settings/components'
import type {
  EditVehicleServicePlanForm,
  EditVehicleServicePlanPromptResolve,
  VehicleServicePlanStatus,
} from '@/modules/vehicles/types'
import type { VehicleServicePlanNode } from '@/modules/vehicles/types.graphql'
import {
  combineTimeSpent,
  getVehicleExpirationTypeName,
  splitTimeSpent,
} from '@/modules/vehicles/util'
import { format } from '@/utils/date-fns'
import { useState } from 'react'
import { VehicleExpirationTypeSelect } from './VehicleExpirationTypeSelect'

interface EditVehicleServicePlanModalProps {
  servicePlan?: VehicleServicePlanNode
  /**
   * The expiration a new service is created from. Prefills the form from it and
   * warns when the service ends after the expiration date.
   */
  expiration?: VehicleExpirationNode
  /**
   * Whether to ask which vehicle the service belongs to. On for the fleet-wide
   * services page, off on a vehicle's own tab, which supplies it itself.
   */
  askForVehicle?: boolean
  onSubmit: PromptResolve<EditVehicleServicePlanPromptResolve | null>
}

export const EditVehicleServicePlanModal: React.FC<
  EditVehicleServicePlanModalProps
> = ({ servicePlan, expiration, askForVehicle = false, onSubmit }) => {
  const translations = useTranslate({
    save: 'common.save',
    type: 'common.type',
    name: 'common.name',
    startDate: 'common.start-date',
    endDate: 'common.end-date',
    newVehicleService: 'vehicles.new-vehicle-service',
    editVehicleService: 'vehicles.edit-vehicle-service',
    description: 'common.description',
    workshop: 'vehicles.service.workshop',
    vehicle: 'common.vehicle',
    cost: 'vehicles.service.cost',
    timeSpent: 'vehicles.service.time-spent',
    hours: 'common.hours',
    minutes: 'common.minutes',
    status: 'common.status',
    hasNotification: 'settings.has-notification',
    notificationTime: 'vehicles.service.notification-time',
    responsibleUser: 'common.responsible-user',
    statuses: {
      planned: 'vehicles.service.status.planned',
      booked: 'vehicles.service.status.booked',
      performed: 'vehicles.service.status.performed',
      cancelled: 'vehicles.service.status.cancelled',
    },
    afterExpiration: ['vehicles.service.after-expiration-date', { date: '' }],
    validation: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  const editing = typeof servicePlan !== 'undefined'
  const [hasNotification, setHasNotification] = useState(
    !!(servicePlan ?? expiration)?.notificationTime
  )

  const expirationDate = expiration ? new Date(expiration.expirationDate) : null

  const scheduledStart = servicePlan?.scheduledStart
    ? new Date(servicePlan.scheduledStart)
    : expirationDate ?? new Date()
  const scheduledEnd = servicePlan?.scheduledEnd
    ? new Date(servicePlan.scheduledEnd)
    : scheduledStart

  const storedNotificationTime = (servicePlan ?? expiration)?.notificationTime
  const notificationTime = !storedNotificationTime
    ? 1
    : differenceInWeeks(
        scheduledStart,
        startOfDay(new Date(storedNotificationTime))
      )

  const statusOptions: {
    value: VehicleServicePlanStatus
    label: string
  }[] = [
    { value: 'PLANNED', label: translations.statuses.planned },
    { value: 'BOOKED', label: translations.statuses.booked },
    { value: 'PERFORMED', label: translations.statuses.performed },
    { value: 'CANCELLED', label: translations.statuses.cancelled },
  ]

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    submitHandler: submit,
    formChangeHandler: handler,
  } = useForm<EditVehicleServicePlanForm>({
    values: {
      name:
        servicePlan?.name ??
        (expiration
          ? getVehicleExpirationTypeName({ expiration, fallback: '' })
          : ''),
      description: servicePlan?.description ?? expiration?.description ?? '',
      scheduledStart,
      scheduledEnd,
      status: (servicePlan?.status as VehicleServicePlanStatus) ?? 'PLANNED',
      workshopName: servicePlan?.workshopName ?? '',
      serviceType:
        servicePlan?.serviceType?.id || expiration?.expirationType?.id || null,
      cost: servicePlan?.cost ?? null,
      timeSpent: servicePlan?.timeSpent ?? null,
      notificationTime,
      responsibleUser:
        servicePlan?.responsibleUser?.id ||
        expiration?.responsibleUser?.id ||
        null,
      vehicle: servicePlan?.vehicle?.id ?? expiration?.vehicle?.id ?? null,
    },
    validators: {
      name: value => (value ? null : translations.validation.required),
      vehicle: value =>
        !askForVehicle || value ? null : translations.validation.required,
      scheduledEnd: (value, form) => {
        if (!value || !form.scheduledStart) return null
        return value >= form.scheduledStart
          ? null
          : translations.validation.startBeforeEnd
      },
    },
    config: {
      initAsInvalid: true,
    },
  })

  const handleChangeDate = (
    field: 'scheduledStart' | 'scheduledEnd',
    date: Date
  ) => {
    const current = form[field]
    handler(field)(
      set(date, {
        hours: current.getHours(),
        minutes: current.getMinutes(),
      })
    )
  }

  // Stored as one number of minutes, entered as hours plus minutes.
  const { hours: hoursSpent, minutes: minutesSpent } = splitTimeSpent(
    form.timeSpent
  )

  function handleChangeTimeSpent(
    part: 'hours' | 'minutes',
    value: number | undefined
  ) {
    handler('timeSpent')(
      combineTimeSpent(
        part === 'hours' ? value : hoursSpent,
        part === 'minutes' ? value : minutesSpent
      )
    )
  }

  function handleChangeServiceType(
    id: string,
    _type: VehicleExpirationTypeShallow | null
  ) {
    handler('serviceType')(id)
  }

  return (
    <RegularModal
      className="shadow-lg"
      title={
        editing
          ? translations.editVehicleService
          : translations.newVehicleService
      }
      width="480px"
      submitText={translations.save}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form, hasNotification }))}
    >
      <Form>
        {askForVehicle && (
          <Field>
            <label>{translations.vehicle}</label>
            <VehicleSelect
              value={form.vehicle ?? ''}
              className="mt-2"
              clearable
              onChange={vehicle => handler('vehicle')(vehicle?.id ?? null)}
            />
            {errors.vehicle && (
              <span className="text-sm text-red-600">{errors.vehicle}</span>
            )}
          </Field>
        )}

        <Field>
          <label>{translations.name}</label>
          <Input
            value={form.name}
            error={errors.name}
            fullWidth
            autoFocus
            onChange={handler('name')}
          />
        </Field>

        <Field>
          <label>{translations.startDate}</label>
          <div className="mt-2 flex gap-2">
            <TimePicker
              value={form.scheduledStart}
              onChange={handler('scheduledStart')}
            />
            <DatePicker
              value={form.scheduledStart}
              fullWidth
              onChange={value => handleChangeDate('scheduledStart', value)}
            />
          </div>
        </Field>

        <Field>
          <label>{translations.endDate}</label>
          <div className="mt-2 flex gap-2">
            <TimePicker
              value={form.scheduledEnd}
              onChange={handler('scheduledEnd')}
            />
            <DatePicker
              value={form.scheduledEnd}
              fullWidth
              onChange={value => handleChangeDate('scheduledEnd', value)}
            />
          </div>
          {errors.scheduledEnd && (
            <span className="text-sm text-red-600">{errors.scheduledEnd}</span>
          )}
          {expirationDate &&
            differenceInCalendarDays(form.scheduledEnd, expirationDate) > 0 && (
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <FAIcon icon="exclamation-triangle" />
                {translations.afterExpiration({
                  date: format(expirationDate, 'PP'),
                })}
              </div>
            )}
        </Field>

        <Field>
          <label>{translations.status}</label>
          <Select
            value={form.status}
            options={statusOptions}
            fullWidth
            grayed
            onChange={(value: VehicleServicePlanStatus | null) => {
              if (value) handler('status')(value)
            }}
          />
        </Field>

        <Field>
          <label>{translations.workshop}</label>
          <Input
            value={form.workshopName}
            fullWidth
            onChange={handler('workshopName')}
          />
        </Field>

        <Field>
          <label>{translations.cost}</label>
          <NullableNumberInput
            value={form.cost ?? undefined}
            className="mt-2"
            min={0}
            placeholder="0"
            onChange={value => handler('cost')(value ?? null)}
          />
        </Field>

        <Field>
          <label>{translations.timeSpent}</label>
          <div className="mt-2 flex gap-2">
            <NullableNumberInput
              value={hoursSpent}
              className="w-full"
              min={0}
              placeholder={translations.hours}
              onChange={value => handleChangeTimeSpent('hours', value)}
            />
            <NullableNumberInput
              value={minutesSpent}
              className="w-full"
              min={0}
              max={59}
              placeholder={translations.minutes}
              onChange={value => handleChangeTimeSpent('minutes', value)}
            />
          </div>
        </Field>

        <Field>
          <label>{translations.type}</label>
          <VehicleExpirationTypeSelect
            fullWidth
            value={form.serviceType}
            onChange={handleChangeServiceType}
          />
        </Field>

        <Field>
          <label>{translations.description}</label>
          <Input
            value={form.description}
            fullWidth
            onChange={handler('description')}
          />
        </Field>

        <div className="flex justify-between p-4">
          <Field>
            <Checkbox
              checked={hasNotification}
              label={translations.hasNotification}
              boxSize="32px"
              onChange={setHasNotification}
            />
          </Field>
        </div>

        {hasNotification && (
          <>
            <Field>
              <label>{translations.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser}
                fullWidth
                onChange={handler('responsibleUser')}
                nullable
              />
            </Field>

            <Field>
              <label>{translations.notificationTime}</label>
              <NumberInput
                value={form.notificationTime ?? 1}
                min={0}
                fullWidth
                onChange={handler('notificationTime')}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
